import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { uploadSignalFile, startSignalImport, getImportStatus, cancelSignalImport, getImportedSignals } from '../services/signalImportService';

export interface ImportedSignal {
  signal_id: string;
  customer_name?: string;
  customer_email?: string;
  channel_type?: string;
  raw_text?: string;
  verbatim?: string;
  classification?: {
    labels?: Array<{
      id: string;
      label: string;
      confidence: number;
    }>;
  };
  created_at: string;
}

export interface ImportSession {
  sessionId: string;
  fileName: string;
  signalCount: number;
  status: 'pending' | 'processing' | 'complete' | 'error' | 'cancelled';
  timestamp: string;
}

type ImportState = 'idle' | 'uploading' | 'processing' | 'complete' | 'error' | 'cancelled';

interface ImportProgress {
  processed: number;
  total: number;
  percentage: number;
  current_batch: number;
  total_batches: number;
}

export const useSignalImport = () => {
  const { addMessageListener, isConnected } = useWebSocket();
  
  // State
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [importState, setImportState] = useState<ImportState>('idle');
  const [progress, setProgress] = useState(0);
  const [signals, setSignals] = useState<ImportedSignal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedBatches, setCompletedBatches] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  const [recentImports, setRecentImports] = useState<ImportSession[]>([]);
  
  // Refs for tracking
  const startTimeRef = useRef<number | null>(null);
  const organizationIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Fallback status poll — WebSocket progress messages are fire-and-forget, so
  // a fast import can complete before the browser's subscription is live and
  // the bar would hang. Polling the session status reconciles that.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Load organization ID from localStorage
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        organizationIdRef.current = userData.organization_id;
      } catch (e) {
        console.error('Failed to parse user data:', e);
      }
    }
  }, []);

  // Update sessionIdRef when sessionId changes
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Load recent imports from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentImports');
    if (stored) {
      try {
        setRecentImports(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent imports:', e);
      }
    }
  }, []);

  // Save recent imports to localStorage
  const saveRecentImport = useCallback((session: ImportSession) => {
    setRecentImports(prev => {
      const updated = [session, ...prev.filter(s => s.sessionId !== session.sessionId)].slice(0, 10);
      localStorage.setItem('recentImports', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Subscribe to WebSocket messages for import updates
  useEffect(() => {
    if (!isConnected) {
      console.log('[SIGNAL-IMPORT-DEBUG] WebSocket not connected yet');
      return;
    }

    console.log('[SIGNAL-IMPORT-DEBUG] Setting up WebSocket listeners, sessionId:', sessionId);

    const handleMessage = (message: any) => {
      // Only log import-related messages
      if (message.type && message.type.startsWith('import.')) {
        console.log('[SIGNAL-IMPORT-DEBUG] Received import message:', message);
      }
      
      // Handle import progress messages
      if (message.type === 'import.progress' || 
          message.type === 'import.started' || 
          message.type === 'import.batch_complete' ||
          message.type === 'import.complete' ||
          message.type === 'import.error' ||
          message.type === 'import.cancelled') {
        
        // Check if message is for our import session
        if (sessionIdRef.current && message.session_id !== sessionIdRef.current) {
          console.log('[SIGNAL-IMPORT-DEBUG] Import message not for our session, ignoring. Our session:', sessionIdRef.current, 'Message session:', message.session_id);
          return;
        }

        console.log('[SIGNAL-IMPORT-DEBUG] Processing import message:', message.type, 'for session:', message.session_id, 'State before:', importState);

        switch (message.type) {
          case 'import.started':
            setImportState('processing');
            setIsProcessing(true);
            startTimeRef.current = Date.now();
            setTotalBatches(message.total_batches || 0);
            break;

          case 'import.progress':
            // Update progress from the message directly
            const progressPercent = message.progress_percent || 0;
            setProgress(progressPercent);
            
            if (message.current_batch !== undefined) {
              setCompletedBatches(message.current_batch);
            }
            if (message.total_batches !== undefined) {
              setTotalBatches(message.total_batches);
            }
            
            // Estimate time remaining
            if (startTimeRef.current && progressPercent > 0) {
              const elapsed = (Date.now() - startTimeRef.current) / 1000;
              const rate = progressPercent / elapsed;
              const remaining = (100 - progressPercent) / rate;
              setEstimatedTimeRemaining(remaining);
            }
            break;

          case 'import.batch_complete':
            if (message.signals && Array.isArray(message.signals)) {
              setSignals(prev => [...prev, ...message.signals]);
            }
            if (message.batch_number) {
              setCompletedBatches(message.batch_number);
            }
            break;

          case 'import.complete':
            setImportState('complete');
            setIsProcessing(false);
            setProgress(100);
            setEstimatedTimeRemaining(null);
            
            // Save to recent imports
            if (message.total_imported && sessionIdRef.current) {
              saveRecentImport({
                sessionId: sessionIdRef.current,
                fileName: message.file_name || 'Unknown',
                signalCount: message.total_imported,
                status: 'complete',
                timestamp: new Date().toISOString()
              });
            }
            
            // Fetch all imported signals
            if (sessionIdRef.current) {
              fetchImportedSignals(sessionIdRef.current);
            }
            break;

          case 'import.error':
            setImportState('error');
            setIsProcessing(false);
            setError(message.error || 'An error occurred during import');
            
            if (sessionIdRef.current) {
              saveRecentImport({
                sessionId: sessionIdRef.current,
                fileName: message.file_name || 'Unknown',
                signalCount: signals.length,
                status: 'error',
                timestamp: new Date().toISOString()
              });
            }
            break;

          case 'import.cancelled':
            setImportState('cancelled');
            setIsProcessing(false);
            setEstimatedTimeRemaining(null);
            
            if (sessionIdRef.current) {
              saveRecentImport({
                sessionId: sessionIdRef.current,
                fileName: message.file_name || 'Unknown',
                signalCount: signals.length,
                status: 'cancelled',
                timestamp: new Date().toISOString()
              });
            }
            break;
        }
      }
    }

    // Add message listener - no need to manually subscribe to organization
    // The backend auto-subscribes to org:{organization_id} channel on connection
    const cleanup = addMessageListener(handleMessage);
    console.log('[SIGNAL-IMPORT-DEBUG] Added WebSocket message listener');
    console.log('[SIGNAL-IMPORT-DEBUG] Organization ID:', organizationIdRef.current);
    console.log('[SIGNAL-IMPORT-DEBUG] Note: Backend auto-subscribes to org channel on connection');

    return () => {
      console.log('[SIGNAL-IMPORT-DEBUG] Cleaning up WebSocket listeners');
      cleanup();
    };
  }, [isConnected, addMessageListener]);

  // Fetch imported signals
  const fetchImportedSignals = useCallback(async (sessionId: string) => {
    try {
      const importedSignals = await getImportedSignals(sessionId);
      if (importedSignals && importedSignals.signals) {
        setSignals(importedSignals.signals);
      }
    } catch (err) {
      console.error('Failed to fetch imported signals:', err);
    }
  }, []);

  // Upload file
  const uploadFile = useCallback(async (file: File, datasetId: string): Promise<string | null> => {
    console.log('[SIGNAL-IMPORT-DEBUG] Starting file upload, file:', file.name, 'size:', file.size, 'datasetId:', datasetId);
    setIsUploading(true);
    setError(null);
    setImportState('uploading');

    try {
      const response = await uploadSignalFile(file, datasetId);
      console.log('[SIGNAL-IMPORT-DEBUG] Upload response:', response);
      
      if (response && response.session_id) {
        console.log('[SIGNAL-IMPORT-DEBUG] Setting session ID:', response.session_id);
        setSessionId(response.session_id);
        setImportState('idle'); // Reset to idle, start will set to processing
        return response.session_id;
      } else {
        console.error('[SIGNAL-IMPORT-DEBUG] Invalid upload response - no session ID');
        throw new Error('No session ID returned from upload');
      }
    } catch (err) {
      console.error('[SIGNAL-IMPORT-DEBUG] Upload failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
      setError(errorMessage);
      setImportState('error');
      return null;
    } finally {
      setIsUploading(false);
      console.log('[SIGNAL-IMPORT-DEBUG] Upload process completed, isUploading set to false');
    }
  }, []);

  // Start import
  const startImport = useCallback(async (sessionId: string) => {
    console.log('[SIGNAL-IMPORT-DEBUG] Starting import for session:', sessionId);
    setError(null);
    
    // Don't change state if we're already processing
    if (importState !== 'processing') {
      setImportState('processing');
      setIsProcessing(true);
      startTimeRef.current = Date.now();
      console.log('[SIGNAL-IMPORT-DEBUG] Set state to processing, isProcessing=true');
    }

    try {
      console.log('[SIGNAL-IMPORT-DEBUG] Calling startSignalImport API...');
      const result = await startSignalImport(sessionId);
      console.log('[SIGNAL-IMPORT-DEBUG] Start import API response:', result);

      // Fallback: poll the session status in case WebSocket progress/complete
      // messages are missed (they aren't replayed). Reconciles the UI so it
      // never hangs at 0% for an import that actually finished server-side.
      clearPoll();
      pollRef.current = setInterval(async () => {
        try {
          const st: any = await getImportStatus(sessionId);
          const s = st?.status;
          if (s === 'completed' || s === 'complete') {
            clearPoll();
            setImportState('complete');
            setIsProcessing(false);
            setProgress(100);
            setEstimatedTimeRemaining(null);
            if (sessionIdRef.current) fetchImportedSignals(sessionIdRef.current);
          } else if (s === 'failed' || s === 'error') {
            clearPoll();
            setImportState('error');
            setIsProcessing(false);
            setError(st?.error_message || st?.error || 'Import failed');
          } else {
            const total = st?.total_rows ?? st?.progress?.total ?? 0;
            const proc = st?.processed_rows ?? st?.progress?.processed ?? 0;
            if (total > 0) setProgress(Math.round((proc / total) * 100));
          }
        } catch {
          /* transient — keep polling */
        }
      }, 2500);
    } catch (err) {
      console.error('[SIGNAL-IMPORT-DEBUG] Start import failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start import';
      setError(errorMessage);
      setImportState('error');
      setIsProcessing(false);
      console.log('[SIGNAL-IMPORT-DEBUG] Set error state:', errorMessage);
    }
  }, [importState, isProcessing]);

  // Cancel import
  const cancelImport = useCallback(async (sessionId: string) => {
    clearPoll();
    try {
      await cancelSignalImport(sessionId);
      setImportState('cancelled');
      setIsProcessing(false);
    } catch (err) {
      console.error('Failed to cancel import:', err);
    }
  }, [clearPoll]);

  // Stop polling on unmount.
  useEffect(() => clearPoll, [clearPoll]);

  // Get status
  const getStatus = useCallback(async (sessionId: string) => {
    try {
      const status = await getImportStatus(sessionId);
      return status;
    } catch (err) {
      console.error('Failed to get import status:', err);
      return null;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setSessionId(null);
    setImportState('idle');
    setProgress(0);
    setSignals([]);
    setError(null);
    setIsUploading(false);
    setIsProcessing(false);
    setCompletedBatches(0);
    setTotalBatches(0);
    setEstimatedTimeRemaining(null);
    startTimeRef.current = null;
  }, []);

  return {
    // State
    sessionId,
    importState,
    progress,
    signals,
    error,
    isUploading,
    isProcessing,
    completedBatches,
    totalBatches,
    estimatedTimeRemaining,
    recentImports,

    // Actions
    uploadFile,
    startImport,
    cancelImport,
    getStatus,
    clearError,
    reset,

    // WebSocket status
    isConnected
  };
};